import React, { useState, useEffect, useCallback } from "react";
import {
  EditButton,
  List,
  ShowButton,
  useDataGrid,
} from "@refinedev/mui";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  IResourceComponentsProps,
  useTranslate,
  useNotification,
} from "@refinedev/core";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Avatar,
  Stack,
  LinearProgress,
  Paper,
  Alert,
  Tooltip,
  useMediaQuery,
  Tab,
  Tabs,
  alpha,
  InputAdornment,
} from "@mui/material";
import {
  PersonAdd,
  Upload,
  Search,
  CheckCircle,
  Cancel,
  ContentCopy,
  Group,
  GroupAdd,
  Email,
  Phone,
  Download,
  Close,
  Send,
} from '@mui/icons-material';
import axiosInstance from "../../api/axios-config";
import { useAuth } from "../../contexts/auth-context";
import { canModifyResource, UserRole } from "../../utils/role-permissions";
import { generateSimplePassword } from "../../utils/password-generator";
import { useNavigate } from "react-router-dom";

export interface IUser {
  id: number;
  email: string;
  gender?: string;
  fullname: string;
  phone?: string;
  hasBeenOnboarded: boolean;
  profilePicture?: string;
  createdAt?: string;
}


export const UserList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const { open } = useNotification();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const isMedium = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const isLarge = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });
  const isXLarge = useMediaQuery(theme.breakpoints.down('xl'), { noSsr: true });
  const navigate = useNavigate();

  // Create responsive key for forcing re-renders
  const responsiveKey = `${isMobile}-${isMedium}-${isLarge}-${isXLarge}`;

  // Check if user can modify resources
  const canModify = currentUser?.role ? canModifyResource(currentUser.role as UserRole, 'users') : false;
  const isVigilane = currentUser?.role === 'VIGILANE';

  // Check if user is GUEST without company
  const isGuestWithoutCompany = currentUser?.role === 'GUEST' && !currentUser?.entrepriseId;

  const [isLoading, setIsLoading] = useState(true);
  const [enterpriseUsers, setEnterpriseUsers] = useState<IUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<IUser[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [tabValue, setTabValue] = useState(0);

  // État pour l'invitation rapide
  const [quickInviteDialog, setQuickInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);

  // État pour l'import CSV
  const [csvDialog, setCsvDialog] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Company usage/limit state
  const [companyLimit, setCompanyLimit] = useState<number | null>(null);
  const [companyUserCount, setCompanyUserCount] = useState<number | null>(null);
  const isFreeTrialModerator = currentUser?.role === 'MODERATOR_FREE';
  const effectiveLimit = isFreeTrialModerator ? 10 : (companyLimit ?? null);
  const usedSeats = companyUserCount ?? null;
  // QR invite dialog state
  const [qrDialog, setQrDialog] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [companies, setCompanies] = useState<Array<{ id: number; nom: string }>>([]);
  // Invite method picker dialog
  const [inviteMethodDialog, setInviteMethodDialog] = useState(false);

  // Stats
  const totalUsers = enterpriseUsers.length;
  const onboardedUsers = enterpriseUsers.filter(u => u.hasBeenOnboarded).length;
  const pendingUsers = totalUsers - onboardedUsers;


  const fetchEnterpriseUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('Current user:', currentUser);
      console.log('Current user role:', currentUser?.role);
      console.log('Current user entrepriseId:', currentUser?.entrepriseId);

      const response = await axiosInstance.get('/users');
      console.log('Users API response:', response.data);

      if (response.data && Array.isArray(response.data)) {
        // Ne plus filtrer l'utilisateur actuel pour unifier le comptage avec CompanyShow
        setEnterpriseUsers(response.data);
        setFilteredUsers(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch enterprise users:", error);
      setEnterpriseUsers([]);
      setFilteredUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    // Don't fetch users if GUEST without company
    if (!isGuestWithoutCompany) {
      fetchEnterpriseUsers();
    } else {
      setIsLoading(false);
      setEnterpriseUsers([]);
      setFilteredUsers([]);
    }
  }, [currentUser, isGuestWithoutCompany, fetchEnterpriseUsers]);

  // Fetch company usage and limit (for seat/limit UI)
  useEffect(() => {
    const fetchCompanyUsage = async () => {
      try {
        const entrepriseId = currentUser?.entrepriseId;
        if (!entrepriseId) return;
        const { data } = await axiosInstance.get(`/company/count/${entrepriseId}`);
        // data: { company: { limitUser: number, ... }, count: number }
        if (data) {
          setCompanyLimit(typeof data.company?.limitUser === 'number' ? data.company.limitUser : null);
          setCompanyUserCount(typeof data.count === 'number' ? data.count : null);
        }
      } catch (e) {
        console.error('Failed to fetch company usage:', e);
      }
    };

    if (!isGuestWithoutCompany) {
      fetchCompanyUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.entrepriseId]);

  useEffect(() => {
    let filtered = [...enterpriseUsers];

    // Filtrer par recherche
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm)
      );
    }

    // Filtrer par statut (tabs)
    if (tabValue === 1) {
      filtered = filtered.filter(user => user.hasBeenOnboarded);
    } else if (tabValue === 2) {
      filtered = filtered.filter(user => !user.hasBeenOnboarded);
    }

    setFilteredUsers(filtered);
  }, [searchTerm, tabValue, enterpriseUsers]);

  // Auto-update invite link when company selection changes in QR dialog
  useEffect(() => {
    if (!qrDialog) return;
    const cid = selectedCompanyId || currentUser?.entrepriseId;
    if (cid) {
      loadPersistentInviteLink(cid);
    }
  }, [qrDialog, selectedCompanyId, currentUser?.entrepriseId]);

  const handleQuickInvite = async () => {
    setInviteLoading(true);
    try {
      // Prevent inviting if limit reached
      if (effectiveLimit != null && usedSeats != null && usedSeats >= effectiveLimit) {
        open?.({
          type: "error",
          message: translate("users.limit.reached", "Limite atteinte"),
          description: translate(
            "users.limit.upgrade_desc",
            "Vous avez atteint la limite d'utilisateurs de votre abonnement. Mettez à niveau votre offre pour inviter plus d'utilisateurs."
          ),
        });
        return;
      }

      // Récupérer l'entreprise du current user si elle n'est pas déjà dans le contexte
      let entrepriseId = currentUser?.entrepriseId;

      console.log('[UserListNew.handleQuickInvite] Initial entrepriseId:', entrepriseId);
      console.log('[UserListNew.handleQuickInvite] Current user:', {
        role: currentUser?.role,
        entrepriseId: currentUser?.entrepriseId,
      });

      // Si l'utilisateur est un MODERATOR sans entrepriseId dans le contexte,
      // on doit la récupérer depuis l'API
      if (!entrepriseId && currentUser?.role === 'MODERATOR') {
        try {
          const userResponse = await axiosInstance.get('/auth/me');
          entrepriseId = userResponse.data.entrepriseId;
          console.log('[UserListNew.handleQuickInvite] Fetched entrepriseId from API:', entrepriseId);
        } catch (error) {
          console.error('[UserListNew.handleQuickInvite] Error fetching user data:', error);
        }
      }

      const requestData = {
        email: inviteEmail,
        fullname: inviteName,
        password: generateSimplePassword(), // Générer un mot de passe simple (6 caractères alphanumériques)
        entrepriseId: entrepriseId,
      };

      console.log('[UserListNew.handleQuickInvite] Sending request to backend:', requestData);

      const response = await axiosInstance.post('/users/create-user', requestData);

      console.log('[UserListNew.handleQuickInvite] Response from backend:', {
        status: response.status,
        data: response.data,
      });

      open?.({
        type: "success",
        message: translate("users.invite.success"),
        description: translate("users.invite.success_desc", { name: inviteName }),
      });

      // Réinitialiser et fermer
      setInviteEmail("");
      setInviteName("");
      setQuickInviteDialog(false);
      fetchEnterpriseUsers();
      // Refresh company usage after invite
      try {
        if (currentUser?.entrepriseId) {
          const { data } = await axiosInstance.get(`/company/count/${currentUser.entrepriseId}`);
          if (data) {
            setCompanyLimit(typeof data.company?.limitUser === 'number' ? data.company.limitUser : companyLimit);
            setCompanyUserCount(typeof data.count === 'number' ? data.count : companyUserCount);
          }
        }
      } catch (e) {
        console.warn('Failed to refresh company usage after invite');
      }
    } catch (error: any) {
      console.error('[UserListNew.handleQuickInvite] ERROR:', error);
      console.error('[UserListNew.handleQuickInvite] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      open?.({
        type: "error",
        message: translate("users.invite.error"),
        description: error.response?.data?.message || translate("common.error.unknown"),
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!file) return;

    // Prevent upload if limit reached
    if (effectiveLimit != null && usedSeats != null && usedSeats >= effectiveLimit) {
      open?.({
        type: "error",
        message: translate("users.limit.reached", "Limite atteinte"),
        description: translate(
          "users.limit.no_remaining",
          "Aucune invitation restante. Mettez à niveau votre offre pour importer de nouveaux utilisateurs."
        ),
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setUploadProgress(0);

    try {
      await axiosInstance.post('/users/csv-import', formData, {
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(progress);
        },
      });

      open?.({
        type: "success",
        message: translate("users.import.success"),
        description: translate("users.import.success_desc"),
      });

      setCsvDialog(false);
      setFile(null);
      fetchEnterpriseUsers();
    } catch (error) {
      open?.({
        type: "error",
        message: translate("users.import.error"),
        description: translate("users.import.error_desc"),
      });
    }
  };

  const downloadExampleCSV = () => {
    const csvContent = [
      "email,fullname,phone",
      "jean.dupont@exemple.com,Jean Dupont,+33612345678",
      "marie.martin@exemple.com,Marie Martin,+33698765432",
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'exemple_import_utilisateurs.csv');
    link.click();
  };

  const openQrDialog = async () => {
    setInviteLink(null);
    setSelectedCompanyId(currentUser?.entrepriseId || null);
    setQrDialog(true);
    // If no company id (e.g., ADMIN), fetch companies to choose from
    if (!currentUser?.entrepriseId) {
      try {
        const res = await axiosInstance.get('/company');
        if (Array.isArray(res.data)) {
          setCompanies(res.data);
          if (res.data.length === 1) {
            setSelectedCompanyId(res.data[0].id);
            loadPersistentInviteLink(res.data[0].id);
          }
        }
      } catch (e) {
        console.error('Failed to load companies', e);
      }
    } else {
      // Company known: auto-load persistent link immediately
      loadPersistentInviteLink(currentUser.entrepriseId);
    }
  };

  const loadPersistentInviteLink = (companyId: number) => {
    const origin = window.location.origin;
    const key = `inviteLink:${companyId}`;
    const stored = localStorage.getItem(key);
    const link = stored || `${origin}/invite/${companyId}`;
    if (!stored) {
      localStorage.setItem(key, link);
    }
    setInviteLink(link);
  };

  const copyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      open?.({ type: 'success', message: 'Lien copié dans le presse-papiers' });
    } catch {}
  };

  const columns = React.useMemo<GridColDef<IUser>[]>(
    () => [
      {
        field: "user",
        headerName: translate("users.fields.user"),
        flex: 1,
        minWidth: isMobile ? 200 : 300,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={isMobile ? 1 : 2} alignItems="center">
            <Avatar
              src={row.profilePicture}
              sx={{
                width: isMobile ? 32 : 40,
                height: isMobile ? 32 : 40,
                bgcolor: row.hasBeenOnboarded ? 'success.main' : 'grey.400'
              }}
            >
              {row.fullname?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {row.fullname}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.email}
              </Typography>
            </Box>
            {row.id === currentUser?.id && (
              <Chip
                label={translate("users.me", "Me")}
                size="small"
                color="primary"
                sx={{
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  height: { xs: '20px', sm: '24px' },
                  fontWeight: 600
                }}
              />
            )}
          </Stack>
        ),
      },
      {
        field: "phone",
        headerName: translate("users.fields.phone"),
        width: isMobile ? 120 : 150,
        renderCell: ({ value }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Phone fontSize="small" color="action" />
            <Typography variant="body2">{value || '-'}</Typography>
          </Stack>
        ),
      },
      {
        field: "hasBeenOnboarded",
        headerName: translate("users.fields.status"),
        width: isMobile ? 120 : 150,
        renderCell: ({ value }) => (
          <Chip
            label={value ? translate("users.status.active") : translate("users.status.pending")}
            color={value ? "success" : "warning"}
            size="small"
            icon={value ? <CheckCircle /> : <Cancel />}
          />
        ),
      },
      {
        field: "actions",
        headerName: translate("common.actions"),
        width: isMobile ? 90 : 120,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={isMobile ? 0.5 : 1}>
            {canModify && !isVigilane && (
              <Tooltip title={translate("common.edit")}>
                <EditButton size={isMobile ? 'small' : 'medium'} hideText recordItemId={row.id} />
              </Tooltip>
            )}
            <Tooltip title={translate("common.details")}>
              <ShowButton size={isMobile ? 'small' : 'medium'} hideText recordItemId={row.id} />
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [canModify, translate, isMobile, isVigilane, currentUser?.id]
  );

  const { dataGridProps } = useDataGrid<IUser>({
    resource: "users",
    pagination: { mode: "client" },
  });

  // Show special UI for GUEST users without company
  if (isGuestWithoutCompany) {
    return (
      <Grid container spacing={2} sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 0 } }}>
        <Grid item xs={12}>
          <Card
            sx={{
              maxWidth: 600,
              mx: 'auto',
              textAlign: 'center',
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${theme.palette.background.paper} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <CardContent sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 3
                }}
              >
                <Group sx={{ fontSize: 50, color: theme.palette.primary.main }} />
              </Box>

              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                {translate('users.no_access_title')}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                {translate('users.no_access_desc')}
              </Typography>

              <Alert severity="info" sx={{ maxWidth: 500, mx: 'auto' }}>
                {translate('users.guest_info')}
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} key={responsiveKey}>
      {/* Header avec stats */}
      <Grid item xs={12}>
        <Card
          sx={{
            mb: 3,
            maxWidth: "100%",
            overflow: "hidden",
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )} 0%, ${alpha(theme.palette.primary.dark, 0.2)} 100%)`
                : `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.light,
                    0.1
                  )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 3 },
              "&:last-child": { pb: { xs: 2, sm: 3 } },
            }}
          >
            <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="stretch">
              <Grid item xs={12} xl={8}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  gutterBottom
                  sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
                >
                  {isVigilane
                    ? translate("users.title_vigilane")
                    : translate("users.manage_team")}
                </Typography>
                {isVigilane ? (
                  <></>
                ) : (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {translate("users.manage_team_desc")}
                  </Typography>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={6} lg={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        textAlign: "center",
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(
                          theme.palette.primary.main,
                          0.1
                        )}`,
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="primary"
                        fontWeight={700}
                        sx={{
                          fontSize: { xs: "1.25rem", sm: "1.5rem", lg: "2rem" },
                        }}
                      >
                        {totalUsers}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                      >
                        {translate("users.stats.total")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} lg={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        textAlign: "center",
                        bgcolor: alpha(theme.palette.success.main, 0.05),
                        border: `1px solid ${alpha(
                          theme.palette.success.main,
                          0.2
                        )}`,
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="success.main"
                        fontWeight={700}
                        sx={{
                          fontSize: { xs: "1.25rem", sm: "1.5rem", lg: "2rem" },
                        }}
                      >
                        {onboardedUsers}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                      >
                        {translate("users.stats.active")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} lg={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        textAlign: "center",
                        bgcolor: alpha(theme.palette.warning.main, 0.05),
                        border: `1px solid ${alpha(
                          theme.palette.warning.main,
                          0.2
                        )}`,
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="warning.main"
                        fontWeight={700}
                        sx={{
                          fontSize: { xs: "1.25rem", sm: "1.5rem", lg: "2rem" },
                        }}
                      >
                        {pendingUsers}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                      >
                        {translate("users.stats.pending")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} lg={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        textAlign: "center",
                        bgcolor: alpha(theme.palette.info.main, 0.05),
                        border: `1px solid ${alpha(
                          theme.palette.info.main,
                          0.1
                        )}`,
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="info.main"
                        fontWeight={700}
                        sx={{
                          fontSize: { xs: "1.25rem", sm: "1.5rem", lg: "2rem" },
                        }}
                      >
                        {Math.round((onboardedUsers / totalUsers) * 100) || 0}%
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                      >
                        {translate("users.stats.activation_rate")}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} xl={4} sx={{ alignContent: "end" }}>
                {isVigilane ? (
                  <Stack spacing={2}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PersonAdd />}
                      fullWidth
                      onClick={() => navigate("/vigilane-link")}
                      sx={{
                        height: { xs: "60px", sm: "80px", md: "100px" },
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                        boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                      }}
                    >
                      Ajouter un utilisateur
                    </Button>
                  </Stack>
                ) : canModify ? (
                  <Stack spacing={2}>
                    {/* Seats usage overview */}
                    {effectiveLimit != null && usedSeats != null && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: `1px solid ${alpha(
                            theme.palette.primary.main,
                            0.15
                          )}`,
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="subtitle2">
                              {translate("users.limit.title")}
                            </Typography>
                            <Chip
                              size="small"
                              label={`${usedSeats}/${effectiveLimit}`}
                              color={
                                usedSeats >= effectiveLimit
                                  ? "error"
                                  : usedSeats / effectiveLimit >= 0.8
                                  ? "warning"
                                  : "default"
                              }
                            />
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={
                              effectiveLimit > 0
                                ? Math.min(
                                    100,
                                    Math.round(
                                      (usedSeats / effectiveLimit) * 100
                                    )
                                  )
                                : 0
                            }
                          />
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {translate("users.limit.remaining")}:{" "}
                              {Math.max(0, effectiveLimit - usedSeats)}
                            </Typography>
                            {isFreeTrialModerator && (
                              <Chip
                                size="small"
                                color="info"
                                label={translate(
                                  "users.limit.free_trial",
                                  "Essai gratuit"
                                )}
                              />
                            )}
                          </Stack>
                          {/* Alerts remplacées par des indicateurs compacts pour alléger l'UI */}
                        </Stack>
                      </Paper>
                    )}
                    {/* Bouton principal unique: ouvre une modale avec 3 choix */}
                    <Tooltip
                      title={
                        effectiveLimit != null &&
                        usedSeats != null &&
                        usedSeats >= effectiveLimit
                          ? translate(
                              "users.limit.reached_cta",
                              "Limite atteinte — mettez à niveau pour inviter"
                            )
                          : ""
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<PersonAdd />}
                          fullWidth
                          onClick={() => setInviteMethodDialog(true)}
                          disabled={
                            effectiveLimit != null &&
                            usedSeats != null &&
                            usedSeats >= effectiveLimit
                          }
                          sx={{
                            py: 1.5,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                            boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                            height: '97px'
                          }}
                        >
                          {translate("users.actions.invite", "Invite")}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      {translate("users.guest_readonly")}
                    </Typography>
                  </Alert>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Barre de recherche et filtres */}
      <Grid item xs={12}>
        <Card
          sx={{
            mb: 0,
            maxWidth: "100%",
            overflow: "hidden",
            borderBottomLeftRadius: "0 !important",
            borderBottomRightRadius: "0 !important",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={translate("users.search.placeholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size={isMobile ? "small" : "medium"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md="auto">
                <Tabs
                  value={tabValue}
                  onChange={(_, newValue) => setTabValue(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    "& .MuiTab-root": {
                      minWidth: { xs: "auto", sm: 120 },
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    },
                  }}
                >
                  <Tab
                    label={translate("users.tabs.all", { count: totalUsers })}
                  />
                  <Tab
                    label={translate("users.tabs.active", {
                      count: onboardedUsers,
                    })}
                  />
                  <Tab
                    label={translate("users.tabs.pending", {
                      count: pendingUsers,
                    })}
                  />
                </Tabs>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Liste des utilisateurs */}
      <Grid item xs={12} sx={{ paddingTop: "0 !important" }}>
        <Card
          sx={{
            maxWidth: "100%",
            overflow: "hidden",
            borderTopLeftRadius: "0 !important",
            borderTopRightRadius: "0 !important",
          }}
        >
          <List canCreate={currentUser?.role === "ADMIN"}>
            {isLoading ? (
              <Box
                sx={{
                  height: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: "100%", overflow: "auto" }}>
                <DataGrid
                  {...dataGridProps}
                  rows={filteredUsers}
                  columns={columns}
                  autoHeight
                  density={isMobile ? "compact" : "standard"}
                  rowHeight={isMobile ? 56 : 70}
                  disableRowSelectionOnClick
                  pageSizeOptions={isMobile ? [5, 10, 25] : [10, 25, 50]}
                  columnVisibilityModel={{
                    phone: !isMobile,
                    hasBeenOnboarded: !isMobile,
                  }}
                  disableColumnMenu
                  sx={{
                    minWidth: { xs: "100%", sm: "auto" },
                    "& .MuiDataGrid-cell": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiDataGrid-row": {
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      minWidth: "100%",
                    },
                  }}
                />
              </Box>
            )}
          </List>
        </Card>
      </Grid>

      {/* Dialog: choix de la méthode d'invitation */}
      <Dialog
        open={inviteMethodDialog}
        onClose={() => setInviteMethodDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              {translate(
                "users.invite.choose_method",
                "Choisir une méthode d'invitation"
              )}
            </Typography>
            <IconButton
              onClick={() => setInviteMethodDialog(false)}
              size="small"
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PersonAdd />}
                onClick={() => {
                  setInviteMethodDialog(false);
                  setQuickInviteDialog(true);
                }}
                disabled={
                  effectiveLimit != null &&
                  usedSeats != null &&
                  usedSeats >= effectiveLimit
                }
              >
                {translate("users.invite.manual", "Manuel")}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GroupAdd />}
                onClick={() => {
                  setInviteMethodDialog(false);
                  openQrDialog();
                }}
                disabled={
                  effectiveLimit != null &&
                  usedSeats != null &&
                  usedSeats >= effectiveLimit
                }
              >
                {translate("users.invite.qr.title")}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => {
                  setInviteMethodDialog(false);
                  setCsvDialog(true);
                }}
                disabled={
                  effectiveLimit != null &&
                  usedSeats != null &&
                  usedSeats >= effectiveLimit
                }
              >
                {translate("users.invite.csv", "Import CSV")}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {effectiveLimit != null && usedSeats != null
              ? translate("users.limit.remaining_inline", {
                  count: Math.max(0, effectiveLimit - usedSeats),
                })
              : ""}
          </Typography>
          <Button onClick={() => setInviteMethodDialog(false)}>
            {translate("common.cancel", "Annuler")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'invitation rapide */}
      <Dialog
        open={quickInviteDialog}
        onClose={() => setQuickInviteDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              {translate("users.invite.title")}
            </Typography>
            <IconButton
              onClick={() => setQuickInviteDialog(false)}
              size="small"
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Alert
            severity="info"
            sx={{
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            {translate("users.invite.info")}
            {effectiveLimit != null && usedSeats != null && (
              <>
                <br />
                {translate("users.limit.remaining_inline", {
                  count: Math.max(0, effectiveLimit - usedSeats),
                })}
              </>
            )}
          </Alert>

          <Stack spacing={3}>
            <TextField
              fullWidth
              label={translate("users.fields.fullname")}
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder={translate("users.fields.fullname_placeholder")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonAdd />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label={translate("users.fields.email")}
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={translate("users.fields.email_placeholder")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setQuickInviteDialog(false)}>
            {translate("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleQuickInvite}
            disabled={
              !inviteEmail ||
              !inviteName ||
              inviteLoading ||
              (effectiveLimit != null &&
                usedSeats != null &&
                usedSeats >= effectiveLimit)
            }
            startIcon={
              inviteLoading ? <CircularProgress size={20} /> : <Send />
            }
          >
            {translate("users.invite.submit")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog QR Code Invitation */}
      <Dialog
        open={qrDialog}
        onClose={() => setQrDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              Inviter via QR code
            </Typography>
            <IconButton onClick={() => setQrDialog(false)} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={{ xs: 1.5, sm: 2 }}>
            {!currentUser?.entrepriseId && (
              <TextField
                select
                fullWidth
                label="Entreprise"
                value={selectedCompanyId || ""}
                onChange={(e: any) =>
                  setSelectedCompanyId(Number(e.target.value))
                }
                SelectProps={{ native: true }}
              >
                <option value="" disabled>
                  Sélectionnez une entreprise
                </option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                  </option>
                ))}
              </TextField>
            )}
            <Alert severity="info">{translate("users.invite.qr.info")}</Alert>
            {inviteLink ? (
              <Stack spacing={2} alignItems="center">
                <Box
                  sx={{
                    p: 2,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <img
                    alt="QR"
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                      inviteLink
                    )}`}
                    style={{
                      width: isMobile ? 180 : 200,
                      height: "auto",
                      maxWidth: "100%",
                    }}
                  />
                </Box>
                <TextField
                  fullWidth
                  value={inviteLink}
                  label={translate("users.invite.qr.link_label")}
                  InputProps={{ readOnly: true }}
                />
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={copyInviteLink}
                    startIcon={<ContentCopy />}
                  >
                    Copier le lien
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={() => {
                      const companyIdForLink =
                        selectedCompanyId ||
                        currentUser?.entrepriseId ||
                        "company";
                      const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=512x512&data=${encodeURIComponent(
                        inviteLink || ""
                      )}`;
                      const a = document.createElement("a");
                      a.href = qrSrc;
                      a.download = `safee-invite-${companyIdForLink}.png`;
                      a.target = "_blank";
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }}
                  >
                    {translate("users.invite.qr.download")}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setQrDialog(false)}>
            {translate("users.invite.qr.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'import CSV */}
      <Dialog
        open={csvDialog}
        onClose={() => setCsvDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              {translate("users.import.title")}
            </Typography>
            <IconButton onClick={() => setCsvDialog(false)} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={{ xs: 2, sm: 3 }}>
            <Alert severity="info">
              {translate("users.import.info")}
              {effectiveLimit != null && usedSeats != null && (
                <>
                  <br />
                  {translate("users.import.remaining_note", {
                    count: Math.max(0, effectiveLimit - usedSeats),
                  })}
                </>
              )}
            </Alert>

            <Paper
              sx={{
                p: 4,
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                textAlign: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <Upload sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />

              <input
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{ display: "none" }}
                id="csv-upload"
              />

              <label htmlFor="csv-upload">
                <Button
                  component="span"
                  variant="contained"
                  size="large"
                  startIcon={<Upload />}
                >
                  {translate("users.import.choose_file")}
                </Button>
              </label>

              {file && (
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={file.name}
                    onDelete={() => setFile(null)}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {translate("users.import.uploading", {
                    progress: uploadProgress,
                  })}
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {translate("users.import.csv_format")}:
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor:
                    theme.palette.mode === "dark" ? "grey.900" : "grey.100",
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                }}
              >
                email,fullname,phone
                <br />
                jean.dupont@exemple.com,Jean Dupont,+33612345678
                <br />
                marie.martin@exemple.com,Marie Martin,+33698765432
              </Paper>
            </Box>

            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={downloadExampleCSV}
              fullWidth
            >
              {translate("users.import.download_template")}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCsvDialog(false)}>
            {translate("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleFileUpload}
            disabled={
              !file ||
              (effectiveLimit != null &&
                usedSeats != null &&
                usedSeats >= effectiveLimit)
            }
            startIcon={<Upload />}
          >
            {translate("users.import.submit")}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
